import api from './api';
import { Order, OrderItem } from '../types';

interface CreateOrderData {
    items: {
        product_id: number;
        quantity: number;
        unit_price: number;
        product_name: string;
    }[];
    total_amount: number;
    delivery_address: string;
    delivery_city: string;
    delivery_postal_code: string;
    delivery_phone: string;
    delivery_notes?: string;
    preferred_delivery_date?: string;
    preferred_delivery_time?: 'morning' | 'afternoon' | 'evening';
    payment_method?: 'cash' | 'check' | 'transfer';
}

interface OrderResponse {
    order: Order;
    message: string;
}

interface OrdersResponse {
    orders: Order[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
    };
}

interface OrderDetailsResponse {
    order: Order & {
        items: OrderItem[];
        first_name: string;
        last_name: string;
        email: string;
    };
}

export const orderService = {
    // Create a new order
    createOrder: async (orderData: CreateOrderData): Promise<OrderResponse> => {
        const response = await api.post('/orders', orderData);
        return response.data;
    },

    // Get user's orders
    getUserOrders: async (page: number = 1, limit: number = 10): Promise<OrdersResponse> => {
        const response = await api.get(`/orders/my-orders?page=${page}&limit=${limit}`);
        return response.data;
    },

    // Get order details by ID
    getOrderById: async (orderId: number): Promise<OrderDetailsResponse> => {
        const response = await api.get(`/orders/${orderId}`);
        return response.data;
    },

    // Admin: Get all orders
    getAllOrders: async (params?: {
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
        startDate?: string;
        endDate?: string;
    }): Promise<OrdersResponse> => {
        const queryParams = new URLSearchParams();

        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.status) queryParams.append('status', params.status);
        if (params?.search) queryParams.append('search', params.search);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);

        const response = await api.get(`/admin/orders?${queryParams.toString()}`);
        return response.data;
    },

    // Admin: Update order status
    updateOrderStatus: async (orderId: number, status: string): Promise<{ message: string }> => {
        const response = await api.put(`/orders/${orderId}/status`, { status });
        return response.data;
    },

    // Admin: Get order statistics
    getOrderStats: async (): Promise<{
        stats: {
            total_orders: number;
            pending_orders: number;
            total_revenue: number;
            today_orders: number;
        };
        monthly_stats: Array<{
            month: string;
            order_count: number;
            revenue: number;
        }>;
        status_distribution: Array<{
            status: string;
            count: number;
        }>;
    }> => {
        const response = await api.get('/admin/orders/stats');
        return response.data;
    }
};
